// Phase 18 · Community · Comment thread (nested, iterative render to avoid babel stack overflow on recursive JSX)
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareReply } from "lucide-react";
import VoteButtons from "./VoteButtons";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtRel(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return Math.floor(diff) + "s";
        if (diff < 3600) return Math.floor(diff / 60) + "m";
        if (diff < 86400) return Math.floor(diff / 3600) + "h";
        return Math.floor(diff / 86400) + "d";
    } catch (e) { return ""; }
}

// Flatten tree to a list of {node, depth} so we can render with a simple map()
function flatten(items, depth, out) {
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
        const n = items[i];
        out.push({ node: n, depth: depth });
        flatten(n.replies, depth + 1, out);
    }
}

function CommentRow(props) {
    const node = props.node;
    const depth = props.depth;
    const postId = props.postId;
    const onReplied = props.onReplied;

    const [replying, setReplying] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!replyText.trim()) return;
        setBusy(true);
        try {
            const r = await fetch(API + "/api/community/posts/" + postId + "/comments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ body: replyText, parent_comment_id: node.id }),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Rate limit — slow down" : "Reply failed");
                return;
            }
            setReplyText("");
            setReplying(false);
            if (onReplied) onReplied();
        } catch (e) { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    const indent = depth > 0 ? { marginLeft: (depth * 16) + "px", paddingLeft: "8px", borderLeft: "1px solid #1A1A1A" } : {};

    return (
        <div style={indent} data-testid={"comment-node-" + node.id}>
            <div className="flex gap-2 py-2">
                <VoteButtons targetType="comment" targetId={node.id} initial={node} size="sm" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mb-0.5">
                        <Link to={"/profile/" + node.author_username} className="hover:text-[#CCCCCC]">
                            @{node.author_username}
                        </Link>
                        <span>·</span>
                        <span>{fmtRel(node.created_at)}</span>
                    </div>
                    <p className="text-sm text-neutral-200 whitespace-pre-wrap">{node.body}</p>
                    <button
                        type="button"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#FFFFFF] transition-colors"
                        onClick={() => setReplying(!replying)}
                        data-testid={"comment-reply-btn-" + node.id}
                    >
                        <MessageSquareReply size={12} /> Reply
                    </button>
                    {replying ? (
                        <div className="mt-2 pl-2 border-l border-[#FFFFFF]/30">
                            <textarea
                                className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                rows={3}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply…"
                                data-testid={"comment-reply-input-" + node.id}
                            />
                            <div className="flex justify-end gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => { setReplying(false); setReplyText(""); }}
                                    className="text-xs text-neutral-500 hover:text-neutral-300 px-2 py-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={send}
                                    disabled={busy || !replyText.trim()}
                                    className="text-xs bg-white text-neutral-900 font-semibold px-3 py-1 rounded disabled:opacity-40"
                                    data-testid={"comment-reply-send-" + node.id}
                                >
                                    {busy ? "..." : "Reply"}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function CommentThread(props) {
    const items = props.items || [];
    const postId = props.postId;
    const onChange = props.onChange;

    if (!items.length) {
        return (
            <p className="text-sm text-neutral-500 italic py-6 text-center" data-testid="comments-empty">
                No comments yet. Be the first to reply.
            </p>
        );
    }

    const flat = [];
    flatten(items, 0, flat);

    return (
        <div className="space-y-1" data-testid="comments-tree">
            {flat.map((row) => (
                <CommentRow
                    key={row.node.id}
                    node={row.node}
                    depth={row.depth}
                    postId={postId}
                    onReplied={onChange}
                />
            ))}
        </div>
    );
}
